import { format, isSameDay, parseISO } from 'date-fns';
import type { PharmacyStore, ShiftAssignment, ShiftPosition, ShiftStaff } from '@/mocks/db/types';

// ── 時間軸定数 ───────────────────────────────────────────────
export const DAY_START = 0;
export const DAY_END = 24;
export const SPAN = DAY_END - DAY_START; // 24h
export const HOURS = Array.from({
  length: SPAN
}, (_, i) => DAY_START + i); // 0..23
export const HOUR_OPTIONS = Array.from({
  length: 25
}, (_, i) => i); // 0..24
export const WEEKLY_LIMIT_HOURS = 40; // 法定労働時間（週）

export const DOW_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** 職種ごとのドット色（完成済みクラス文字列で保持し動的結合を避ける）。 */
export const POSITION_STYLE: Record<ShiftPosition, {
  dot: string;
}> = {
  pharmacist: {
    dot: 'bg-decorative-purple-fill'
  },
  registeredSeller: {
    dot: 'bg-decorative-cyan-fill'
  },
  cashier: {
    dot: 'bg-decorative-orange-fill'
  },
  cosmetics: {
    dot: 'bg-decorative-rose-fill'
  },
  stocker: {
    dot: 'bg-decorative-emerald-fill'
  }
};

/** zod enum 等で再利用する職種キーのタプル（順序は表示順）。 */
export const POSITION_KEYS = ['pharmacist', 'registeredSeller', 'cashier', 'cosmetics', 'stocker'] as const satisfies readonly ShiftPosition[];
export const POSITION_ORDER: ShiftPosition[] = [...POSITION_KEYS];

/** 無資格販売リスク判定で有資格扱いとなる職種。 */
const LICENSED_POSITIONS: ShiftPosition[] = ['pharmacist', 'registeredSeller'];
export type Shift = {
  startH: number;
  endH: number;
};
export const GRID_DIVIDER = 'divide-x divide-neutral-muted-border';

// ── 純粋ヘルパー ─────────────────────────────────────────────
export function fmtRange(s: Shift): string {
  return `${s.startH}:00–${s.endH}:00`;
}
export function pct(h: number): number {
  return (h - DAY_START) / SPAN * 100;
}

/** 勤務時間から自動算出する休憩窓（6h 以上はおおむね中間に休憩）。 */
export function breakWindow(s: Shift): Shift | null {
  const dur = s.endH - s.startH;
  if (dur < 6) return null;
  const start = s.startH + Math.floor(dur / 2);
  return {
    startH: start,
    endH: start + 1
  };
}

/** 勤務時間（h）から必要休憩（分）を算出。6h 超→45分 / 8h 超→60分。 */
export function requiredBreakMinutes(workHours: number): number {
  if (workHours > 8) return 60;
  if (workHours > 6) return 45;
  return 0;
}

// ── 日付計算 ─────────────────────────────────────────────────
/** anchor（ISO 文字列）を Date に。失敗時は今日。 */
export function parseAnchor(anchor: string): Date {
  const d = parseISO(anchor);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}
export function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

// ── データマッピング（assignment → グリッド形状） ─────────────
export type GridShift = Shift & {
  assignmentId: string;
  breakStartH: number | null;
  breakEndH: number | null;
  note: string;
};
export type WeeklyEmployeeRowData = {
  staff: ShiftStaff;
  total: string;
  /** Mon..Sun（7 件）。割当が無い日は null。 */
  shifts: (GridShift | null)[];
};
export type WeeklyGroupData = {
  key: ShiftPosition;
  employees: WeeklyEmployeeRowData[];
};
export type DailyEmployeeRowData = {
  staff: ShiftStaff;
  shift?: GridShift;
};
export type DailyGroupData = {
  key: ShiftPosition;
  employees: DailyEmployeeRowData[];
};
function toGridShift(a: ShiftAssignment): GridShift {
  return {
    assignmentId: a.id,
    startH: a.startH,
    endH: a.endH,
    breakStartH: a.breakStartH,
    breakEndH: a.breakEndH,
    note: a.note
  };
}
function staffByPosition(staff: ShiftStaff[]): Map<ShiftPosition, ShiftStaff[]> {
  const map = new Map<ShiftPosition, ShiftStaff[]>();
  for (const k of POSITION_ORDER) map.set(k, []);
  for (const s of staff) {
    const arr = map.get(s.position);
    if (arr) arr.push(s);
  }
  return map;
}

/** 週（Mon..Sun の 7 日）の割当を職種グループごとの行に変換。 */
export function buildWeeklyGroups(staff: ShiftStaff[], assignments: ShiftAssignment[], weekDays: Date[]): WeeklyGroupData[] {
  const byPosition = staffByPosition(staff);
  const isoDays = weekDays.map(toIsoDate);
  return POSITION_ORDER.map(key => {
    const employees = (byPosition.get(key) ?? []).map(s => {
      const shifts = isoDays.map(iso => {
        const a = assignments.find(x => x.staffId === s.id && x.date === iso);
        return a ? toGridShift(a) : null;
      });
      const totalHours = shifts.reduce((sum, sh) => sum + (sh ? sh.endH - sh.startH : 0), 0);
      return {
        staff: s,
        total: `${totalHours}h`,
        shifts
      };
    });
    return {
      key,
      employees
    };
  });
}

/** 指定日の割当を職種グループごとの行に変換。 */
export function buildDailyGroups(staff: ShiftStaff[], assignments: ShiftAssignment[], day: Date): DailyGroupData[] {
  const byPosition = staffByPosition(staff);
  const iso = toIsoDate(day);
  return POSITION_ORDER.map(key => {
    const employees = (byPosition.get(key) ?? []).map(s => {
      const a = assignments.find(x => x.staffId === s.id && x.date === iso);
      return {
        staff: s,
        shift: a ? toGridShift(a) : undefined
      };
    });
    return {
      key,
      employees
    };
  });
}

/** 指定日に割当のあるスタッフ（月次カレンダーの顔表示用）。 */
export function staffOnDay(staff: ShiftStaff[], assignments: ShiftAssignment[], day: Date): ShiftStaff[] {
  const iso = toIsoDate(day);
  const ids = new Set(assignments.filter(a => a.date === iso).map(a => a.staffId));
  return staff.filter(s => ids.has(s.id));
}

/**
 * 無資格販売リスク窓を算出。
 * 営業時間（openHour..closeHour）中に有資格者（pharmacist/registeredSeller かつ qualified）が
 * 1 人も勤務していない時間帯を連続区間として返す。
 */
export function computeRiskIntervals(store: PharmacyStore, staff: ShiftStaff[], assignments: ShiftAssignment[], day: Date): Shift[] {
  const iso = toIsoDate(day);
  const qualifiedStaffIds = new Set(staff.filter(s => s.qualified && LICENSED_POSITIONS.includes(s.position)).map(s => s.id));
  const dayAssignments = assignments.filter(a => a.date === iso && qualifiedStaffIds.has(a.staffId));
  const intervals: Shift[] = [];
  let cur: Shift | null = null;
  for (let h = store.openHour; h < store.closeHour; h++) {
    const covered = dayAssignments.some(a => a.startH <= h && a.endH > h);
    if (!covered) {
      if (cur) cur.endH = h + 1;else cur = {
        startH: h,
        endH: h + 1
      };
    } else if (cur) {
      intervals.push(cur);
      cur = null;
    }
  }
  if (cur) intervals.push(cur);
  return intervals;
}

/** 週内で累計が法定労働時間を初めて超える曜日 index の集合。 */
export function computeWeeklyAlertDays(groups: WeeklyGroupData[]): Set<number> {
  const days = new Set<number>();
  for (const g of groups) {
    for (const e of g.employees) {
      let cum = 0;
      for (let i = 0; i < 7; i++) {
        const s = e.shifts[i];
        if (s) cum += s.endH - s.startH;
        if (cum > WEEKLY_LIMIT_HOURS) {
          days.add(i);
          break;
        }
      }
    }
  }
  return days;
}
export { isSameDay };